import Component from '@glimmer/component'
import { service } from '@ember/service'
import { action } from '@ember/object'
import type RouterService from '@ember/routing/router-service'
import type GradeSetsService from 'teacher-ui-client/services/grade-sets'
import type { IntlService } from 'ember-intl'
import { tracked } from '@glimmer/tracking'
import type { PrintablesTabMeta } from 'teacher-ui-client/subscription-type/printables/tabs'
import { fn } from '@ember/helper'
import { eq } from 'ember-truth-helpers'
import { t } from 'ember-intl'
import { on } from '@ember/modifier'
import { uniq } from 'lodash'
import pick from 'teacher-ui-client/helpers/pick'
import themeKey from 'teacher-ui-client/helpers/theme-key'
import PageHeader from 'teacher-ui-client/components/page-header'
import RadioButtonGroup from 'teacher-ui-client/components/base/radio-button-group'
import InputText from 'teacher-ui-client/components/base/input-text'
import { QuickFilterButton } from 'teacher-ui-client/components/printables/quick-filter-button'
import { PrintablesTab } from 'teacher-ui-client/components/printables/tab'
import type PrintablesRoute from 'teacher-ui-client/subscription-type/printables/route'
import { ResourceCard } from 'teacher-ui-client/components/printables/resource-card'

interface FilterButton {
  value: 'all' | number
  text: string
}

interface Signature {
  Args: {
    model: ModelFor<PrintablesRoute>
  }
}

export class PrintablesPage extends Component<Signature> {
  @service router!: RouterService

  @service gradeSets!: GradeSetsService

  @service intl!: IntlService

  @tracked
  selectedFilterIds: string[] = []

  @tracked
  currentlyFilteredGradePosition: 'all' | number = 'all'

  @tracked
  textFilter: Nullable<string> = null

  get allFilters() {
    return (
      this.args.model.filters?.map((filter) => {
        const isActive = this.selectedFilterIds.includes(filter.id)
        return {
          ...filter,
          isActive,
        }
      }) ?? []
    )
  }

  get gradePositionFilterButtons(): FilterButton[] {
    // There's only books for these grades
    const initialValue: { text: string; value: number }[] = []
    const allowedGradePositions = [1, 2, 3, 4, 5, 6, 7]
    const gradeButtons = this.gradeSets.grades.reduce((buttons, grade) => {
      return allowedGradePositions.includes(grade.position)
        ? [...buttons, { text: grade.fullName, value: grade.position }]
        : buttons
    }, initialValue)
    return [{ text: this.intl.t('all'), value: 'all' }, ...gradeButtons]
  }

  get selectedFilters() {
    return this.allFilters.filter(({ isActive }) => isActive)
  }

  get selectedProductQuickFilters() {
    return this.selectedFilters.filter((quickFilter) => quickFilter.id.startsWith('fields.product='))
  }

  get selectedKeywordQuickFilters() {
    const alphanumeric = /^[A-Za-z0-9\s]+$/
    return this.selectedFilters.filter((quickFilter) => alphanumeric.test(quickFilter.id))
  }

  get searchableBookSets() {
    return this.args.model.bookSets
      .map((bookSet) => {
        const { fields } = bookSet
        const bookSetRoles = uniq(
          fields.books.filter(<T,>(book: T | undefined): book is T => Boolean(book)).map((book) => book.fields.role) ??
            [],
        )
        // description is type Document
        // eslint-disable-next-line @typescript-eslint/no-base-to-string
        const searchableContent = [
          bookSetRoles,
          fields.bookTitle,
          fields.description,
          fields.subject,
          fields.series,
          fields.keywords,
        ]
          .join(' ')
          .toLowerCase()
        return {
          ...bookSet,
          searchableContent,
        }
      })
      .sort((a, b) => a.fields.gradePosition - b.fields.gradePosition)
  }

  isAllowedByGradeFilter = (bookSet: (typeof this.searchableBookSets)[0]) =>
    this.currentlyFilteredGradePosition === 'all'
      ? true
      : bookSet.fields.gradePosition === this.currentlyFilteredGradePosition

  isAllowedByProductsQuickFilter = (bookSet: (typeof this.searchableBookSets)[0]) =>
    this.selectedProductQuickFilters.every(({ id }) => id.split('=')[1] === bookSet.fields.product)

  isAllowedByKeywordsQuickFilter = (bookSet: (typeof this.searchableBookSets)[0]) =>
    this.selectedKeywordQuickFilters.every(({ id }) => bookSet.searchableContent.includes(id.toLowerCase()))

  isAllowedByTextFilter = (bookSet: (typeof this.searchableBookSets)[0]) =>
    this.textFilter === null ? true : bookSet.searchableContent.includes(this.textFilter.trim().toLowerCase())

  get visibleBookSets() {
    return this.searchableBookSets.filter(
      (bookSet) =>
        this.isAllowedByGradeFilter(bookSet) &&
        this.isAllowedByProductsQuickFilter(bookSet) &&
        this.isAllowedByKeywordsQuickFilter(bookSet) &&
        this.isAllowedByTextFilter(bookSet),
    )
  }

  @action
  toggleFilter(filterId: string) {
    if (this.selectedFilterIds.includes(filterId)) {
      this.selectedFilterIds = this.selectedFilterIds.filter((selectedId) => selectedId !== filterId)
    } else {
      this.selectedFilterIds = [...this.selectedFilterIds, filterId]
    }
  }

  @action
  goToPrintablesTab(tab: PrintablesTabMeta) {
    this.selectedFilterIds = []
    this.textFilter = null
    this.currentlyFilteredGradePosition = 'all'
    // query param only transition
    void this.router.transitionTo('subscription-type.printables', { queryParams: { tab: tab.id } })
  }

  @action
  filterByGradePosition(grade: unknown) {
    if (typeof grade === 'number' || grade === 'all') {
      this.currentlyFilteredGradePosition = grade
    }
  }

  @action
  updateTextFilter(value: string | null) {
    this.textFilter = value
  }

  <template>
    <div class="w-full space-y-4">
      <PageHeader>
        <:title>
          <h1>
            {{t "printables.heading"}}
          </h1>
        </:title>
      </PageHeader>
      <div class="mx-12 flex flex-wrap justify-center gap-2">
        {{#each @model.tabs as |tab|}}
          <PrintablesTab
            @tabId={{tab.id}}
            @imageUrl={{tab.imageUrl}}
            @imageAlt={{tab.imageAlt}}
            @isActive={{eq @model.currentTabId tab.id}}
            @title={{t tab.titleKey}}
            class="w-56"
            {{on "click" (fn this.goToPrintablesTab tab)}}
          />
        {{/each}}
      </div>

      <div class="flex flex-wrap items-center justify-center gap-4">
        <RadioButtonGroup
          data-test-grade-position-filter
          class="flex"
          @name="grade-select"
          @buttons={{this.gradePositionFilterButtons}}
          @value={{this.currentlyFilteredGradePosition}}
          @onSelect={{this.filterByGradePosition}}
        />
        <InputText
          data-test-printables-search
          type="search"
          role="searchbox"
          name="search-filter"
          class="w-72"
          value={{this.textFilter}}
          placeholder={{t "printables.search"}}
          aria-label={{t "printables.search"}}
          @themeKey={{themeKey @model.subscriptionType}}
          @icon="search"
          @iconEnd={{true}}
          {{on "input" (pick "target.value" this.updateTextFilter)}}
        />
      </div>

      <div class="flex flex-wrap items-center justify-center gap-4">
        <div>{{t "printables.filter"}}:</div>
        {{#each this.allFilters as |filter index|}}
          <QuickFilterButton
            data-test-quick-filter-button={{index}}
            @toggleFilter={{this.toggleFilter}}
            @filter={{filter.id}}
            @label={{filter.label}}
            @isActive={{filter.isActive}}
          />
        {{/each}}
      </div>

      <div class="flex flex-wrap items-stretch justify-center gap-x-6 gap-y-4">
        {{#each this.visibleBookSets as |bookSet|}}
          <ResourceCard
            data-test-printables-book-set
            class="w-full md:max-w-xs"
            @bookSet={{bookSet}}
            @subscriptionType={{@model.subscriptionType}}
          />
        {{/each}}
      </div>
    </div>
  </template>
}

export default PrintablesPage
